import type { PageTransition } from '@matter_extractor/shared';
import type { CSSProperties, ReactNode } from 'react';
import { pageTransitionCss } from './page-transition';
import type { ReaderMotion } from './reader-theme';

export interface PageTransitionFrameProps {
  /** The transition carried by the edge LEAVING the previous page (§9.3). */
  readonly transition: PageTransition;
  readonly motion: ReaderMotion;
  /** Changes when the page changes: what makes the frame play the entry again. */
  readonly pageKey: string;
  readonly children: ReactNode;
}

/**
 * The arriving page, under its transition (§9.3).
 *
 * A caller that keeps ONE page mounted at a time - the session, which « avance
 * seulement » and has no back - needs the entry and nothing else; this is that
 * caller's whole job done for it. The `key` on the inner element is what
 * replays the animation: a CSS animation runs on the mount, so a new page is a
 * new element.
 *
 * A caller that wants to cross-fade keeps both pages and spends
 * `pageTransitionCss` itself - it hands back `leaveClassName` and `durationMs`
 * for exactly that, and the couple « classe + durée » is the seam §9.3 asks
 * for.
 */
export function PageTransitionFrame({
  transition,
  motion,
  pageKey,
  children,
}: PageTransitionFrameProps): ReactNode {
  const css = pageTransitionCss(transition, motion);
  return (
    <div key={pageKey} className={css.enterClassName} style={css.style as CSSProperties}>
      {children}
    </div>
  );
}
